import { type SelectableTheme, useTheme } from "components/App/ThemeProvider";
import { Avatar } from "components/common/Avatar";
import { BeyondTrustLogoGroup } from "components/common/BeyondTrustLogoGroup";
import { Link as ExternalLink } from "components/common/Link";
import { Divider } from "components/ui/Divider";
import { IconButton } from "components/ui/IconButton";
import { MoonIcon, SunIcon, ThemeIcon } from "components/ui/Icons";
import { HelpIcon } from "components/ui/Icons/HelpIcon";
import { SignOutIcon } from "components/ui/Icons/SignOutIcon";
import { UserCircleIcon } from "components/ui/Icons/UserCircleIcon";
import { Tooltip } from "components/ui/Tooltip";
import { compiledClientConfig } from "config";
import { useAuthenticatedUser } from "hooks/useAuthenticatedUser";
import { useOnClickOutside } from "hooks/useOnClickOutside";
import { useTooltip } from "hooks/useTooltip";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { routes } from "routes/routes";

import { NavbarButton } from "./components/NavbarButton";
import { SelectionOption } from "./components/SelectionOption";
import { TOP_NAV_HEIGHT_PX } from "./constants";

const README_URL = `${compiledClientConfig.beyondtrustDocsUrl}/welcome`;

const THEME_ICONS: Record<SelectableTheme, IconComponent> = {
  light: SunIcon,
  // eslint-disable-next-line perfectionist/sort-objects
  dark: MoonIcon,
  system: ThemeIcon,
};

export const TopNav: FC = () => {
  const { t } = useTranslation();

  const { signOut, user } = useAuthenticatedUser();
  const { setTheme, theme } = useTheme();

  const userActionsRef = useRef<HTMLDivElement>(null);

  const [isUserActionsModalOpen, setIsUserActionsModalOpen] = useState(false);
  const { getTooltipProps, setTooltipRef, setTriggerRef, visible } = useTooltip({
    offset: [0, 6],
    placement: "bottom-start",
    visible: isUserActionsModalOpen,
  });

  const navigate = useNavigate();

  const closeModal = useCallback(() => setIsUserActionsModalOpen(false), []);
  useOnClickOutside(userActionsRef || undefined, closeModal);

  const handleClick = useCallback(() => {
    setIsUserActionsModalOpen((state) => !state);
  }, []);

  const navigateToProfile = useCallback(() => {
    closeModal();
    void navigate(routes.profile.main);
  }, [closeModal, navigate]);

  const handleSignOut = useCallback(() => {
    closeModal();
    void signOut();
  }, [closeModal, signOut]);

  const userButtonPrefix = user?.imageUrl
    ? (
        <Avatar className="max-h-5 max-w-5" user={user} />
      )
    : (
        <UserCircleIcon className="max-h-5 max-w-5" />
      );

  return (
    <div
      className="flex items-center justify-between px-6 bg-sidebar text-sidebar-foreground gap-5"
      style={{ height: TOP_NAV_HEIGHT_PX }}
    >
      <Link to={routes.requests.main}>
        <BeyondTrustLogoGroup />
      </Link>

      <div className="flex items-center justify-start gap-5 max-w-75 min-w-12.5 h-8">
        {user?.isAdmin
          ? (
              <>
                <ExternalLink
                  className="h-7 w-7"
                  external
                  rel="noopener noreferrer"
                  target="_blank"
                  to={README_URL}
                >
                  <Tooltip content={t("navigation.linkToEntitleDocs")} placement="bottom">
                    <IconButton size="large" variant="sidebar">
                      <HelpIcon />
                    </IconButton>
                  </Tooltip>
                </ExternalLink>

                <Divider vertical />
              </>
            )
          : null}

        <div className="flex max-w-75 min-w-12.5" ref={userActionsRef}>
          <NavbarButton active={visible} onClick={handleClick} prefix={userButtonPrefix} ref={setTriggerRef}>
            {user!.fullName}
          </NavbarButton>

          {visible && (
            <div ref={setTooltipRef} {...getTooltipProps()} className="flex flex-col items-start w-53 p-0 z-9999 rounded-md bg-background text-foreground shadow-[0px_8px_7px_7px_rgba(0,0,0,0.20)]">
              <div className="w-full p-3">
                {user?.fullName && <div className="truncate font-semibold" title={user.fullName}>{user.fullName}</div>}
                {user?.email && <div className="truncate text-sm text-muted-foreground" title={user.email}>{user.email}</div>}
              </div>

              <Divider horizontal />

              <div className="w-full px-3 pt-3 font-semibold">{t("navigation.theme.title")}</div>
              {Object.entries(THEME_ICONS).map(([themeMode, ThemeIcon]) => (
                <SelectionOption key={themeMode} onClick={() => setTheme(themeMode as SelectableTheme)} prefix={<ThemeIcon />} selected={theme === themeMode || (themeMode === "system" && theme === "matchSystem")}>
                  {t(`navigation.theme.${themeMode as SelectableTheme}`)}
                </SelectionOption>
              ))}

              <Divider horizontal />

              <SelectionOption onClick={navigateToProfile} prefix={<UserCircleIcon />}>
                {t("navigation.profile")}
              </SelectionOption>

              <SelectionOption onClick={handleSignOut} prefix={<SignOutIcon />}>
                {t("navigation.signOut")}
              </SelectionOption>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
